import React from 'react';
import {useTranslation} from 'react-i18next';
import {isAmount, isPlainObject} from '../shapes';
import {formatMoney, humanizeValue, isNumeric} from '../humanize';
import {extraFieldsSummary, truncateText} from '../diff_field_types';
import {DeltaChip} from '../primitives/DeltaChip';
import {DetailFooter} from '../primitives/DetailFooter';

function stringOrNull(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// El ítem del que se habla define la frase: grupo de anuncios > campaña > presupuesto de la
// línea. El campo consumido no se repite en el pie.
function storyTarget(detail) {
    const adgroup = stringOrNull(detail.adgroup_name);
    if (adgroup) return {key: 'activity_log.detail.budget_story_adgroup', name: adgroup, field: 'adgroup_name'};
    const campaign = stringOrNull(detail.campaign_name);
    if (campaign) return {key: 'activity_log.detail.budget_story_campaign', name: campaign, field: 'campaign_name'};
    return {key: 'activity_log.detail.budget_story', name: null, field: null};
}

function amountOf(value) {
    return isAmount(value) ? value.amount : value;
}

function currencyOf(value) {
    return (isAmount(value) && stringOrNull(value.currency)) || null;
}

// El código ISO sólo aparece cuando hace falta desambiguar (la moneda cambió). Si es la
// misma en los dos lados, "$ 1,000,935.00" alcanza y se lee mejor.
function amountText(value, showIso, t) {
    const amount = amountOf(value);
    if (isNumeric(amount)) return formatMoney(amount, showIso ? currencyOf(value) : null);
    if (amount === null || amount === undefined || amount === '') return '—';
    const humanized = humanizeValue('', amount, t);
    return humanized === null ? '—' : truncateText(humanized);
}

// BUDGET_MODIFIED / CAMPAIGN_BUDGET_MODIFIED / ADGROUP_BUDGET_MODIFIED: un solo dato que
// cambió, así que el panel es UNA frase + el chip de variación. Si cambió la moneda, los
// montos van con ISO y sin chip (no son comparables).
export function BudgetModifiedDetail({detail}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const before = detail.before;
    const after = detail.after;
    const beforeCurrency = currencyOf(before);
    const afterCurrency = currencyOf(after);
    const currencyChanged = !!beforeCurrency && !!afterCurrency && beforeCurrency !== afterCurrency;

    const beforeAmount = amountOf(before);
    const afterAmount = amountOf(after);
    const showDelta = !currencyChanged && isNumeric(beforeAmount) && isNumeric(afterAmount);

    const target = storyTarget(detail);
    const story = t(target.key, {
        name: target.name,
        before: amountText(before, currencyChanged, t),
        after: amountText(after, currencyChanged, t),
        interpolation: {escapeValue: false},
    });
    const extras = extraFieldsSummary(detail, t, ['before', 'after', target.field]);

    return (
        <React.Fragment>
            <div className="ald-story">
                {story}
                {showDelta ? (
                    <React.Fragment>
                        {' '}
                        {/* Sin ISO: el chip acompaña montos que ya están en la misma moneda que la frase. */}
                        <DeltaChip before={beforeAmount} after={afterAmount} money/>
                    </React.Fragment>
                ) : null}
            </div>
            <DetailFooter>{extras}</DetailFooter>
        </React.Fragment>
    );
}
